import { decodeFromBase64 } from '@carboncopyco/editor-utils';
import type { MockResponseModifier } from '@carboncopyco/types';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { array, object } from 'yup';
import { FormBuilder } from '../../../FormBuilder';
import {
  CodeInput,
  ResponseRepresentationType,
  responseRepresentationTypeSchema,
  schemaResolver,
} from '../../../TransactionRuleForm';
import {
  BackButton,
  CancelButton,
  FormLayout,
  Section,
  SubmitButton,
} from '../../../TransactionRuleModal';
import { useCreateTransactionRuleContext } from '../../CreateTransactionRuleContext';

const schema = object({
  representationType: responseRepresentationTypeSchema.required(),
  representationFields: array(),
});

export const CreateResponseVisualizationStep = () => {
  const {
    transactionRule,
    prevStep,
    cancelCreation,
    onLearnMoreClick,
    nextStep,
    updateTransactionRule,
  } = useCreateTransactionRuleContext();
  const methods = useForm({
    defaultValues: {
      representationType: 'form',
      representationFields: [],
    },
    resolver: schemaResolver(schema),
  });
  const { watch, setValue, handleSubmit } = methods;
  const representationType = watch('representationType');
  const fields = watch('representationFields');

  const submitForm = () => {
    const updatedTransactionRule = structuredClone(transactionRule);
    updatedTransactionRule.modifiers[0].arguments = {
      ...updatedTransactionRule.modifiers[0].arguments,
      representationType,
      representationFields: fields,
    };

    updateTransactionRule(updatedTransactionRule);
    nextStep();
  };

  const json = useMemo(() => {
    try {
      const response = decodeFromBase64(
        (transactionRule.modifiers[0] as MockResponseModifier).arguments.content
      );
      return JSON.parse(response);
    } catch {
      return {};
    }
  }, []);

  return (
    <FormProvider {...methods}>
      <FormLayout onSubmit={handleSubmit(submitForm)}>
        <FormLayout.Content>
          <Section
            title="Visual representation"
            iconType="visualization"
          >
            <ResponseRepresentationType hideLabel />
            <div style={{ height: 440 }}>
              {representationType === 'form' ? (
                <FormBuilder
                  value={{ json, fields }}
                  onChange={(value) =>
                    setValue('representationFields', value.fields)
                  }
                  onLearnMoreClick={onLearnMoreClick}
                />
              ) : (
                <CodeInput
                  onMount={(editor) => {
                    editor.updateOptions({
                      readOnly: false,
                    });
                    editor
                      .getAction('editor.action.formatDocument')
                      ?.run()
                      .then(() => editor.revealLine(1))
                      .then(() =>
                        editor.updateOptions({
                          readOnly: true,
                        })
                      );
                  }}
                  value={JSON.stringify(json)}
                  language="json"
                  height={440}
                  options={{ readOnly: true }}
                />
              )}
            </div>
          </Section>
        </FormLayout.Content>
        <FormLayout.Footer>
          <BackButton onClick={prevStep} />
          <div className="flex-1" />
          <CancelButton onClick={cancelCreation} />
          <SubmitButton
            label="Next"
            disabled={representationType === 'form' && !fields.length}
          />
        </FormLayout.Footer>
      </FormLayout>
    </FormProvider>
  );
};
